// ═══════════════════════════════════════════════════════════════════════════
// SIMULATION ENGINE — Shared simulation loop for Hub V2
// Extracted from HubSimulator.tsx + HubBusinessCase.tsx, with:
//  - CHP and CSF as explicit parameters (not hardcoded from profile)
//  - Pre-computed normalization factor (perf improvement)
//  - Single source of truth for both simulator visualization and business case
// ═══════════════════════════════════════════════════════════════════════════

const MD = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const MN = ['Jan','Feb','Mrt','Apr','Mei','Jun','Jul','Aug','Sep','Okt','Nov','Dec'];

export interface BlokstroomConfig {
  enabled: boolean;
  power: number;   // MW
  start: number;   // hour (0-23)
  end: number;     // hour (0-23)
}

function isBlokstroomHour(h: number, blok: BlokstroomConfig): boolean {
  if (!blok.enabled) return false;
  return blok.start < blok.end
    ? (h >= blok.start && h < blok.end)
    : (h >= blok.start || h < blok.end);
}

export interface SimParams {
  SP: number[][];     // solar profile 12x24
  solarRef: number;   // reference solar capacity (kWp)
  pv: number;         // PV capacity (MWp)
  bE: number;         // battery energy (MWh)
  bP: number;         // battery power (MW)
  eff: number;        // round-trip efficiency (%)
  dod: number;        // depth of discharge (%)
  buP: number;        // backup power (MW)
  gMax: number;       // grid max (MW)
  deg: number;        // degradation (%/year)
  projYear: number;   // projection year (1-based)
  blok: BlokstroomConfig;

  // Two-layer demand model:
  // Layer 0: Base load (grid consumption profile)
  demBase: number;     // Annual base consumption (MWh)
  chpBase: number[];   // 24-element hourly shape for base load
  csfBase: number[];   // 12-element seasonal factors for base load
  // Layer 1: EV charging (optional, can be 0)
  demEv: number;       // Annual EV charging demand (MWh)
  chpEv: number[];     // 24-element hourly shape for EV charging
  csfEv: number[];     // 12-element seasonal factors for EV

  // Legacy single-layer (backwards compat — ignored if demBase is set)
  dem?: number;
  chp?: number[];
  csf?: number[];
}

// Summary result (used by financial model)
export interface SimSummary {
  pvProduction: number;
  /** MWh/jaar VRAAG (`tDem`), inclusief het ongedekte deel — geen levering. */
  totalDelivered: number;
  gridImport: number;
  gridExport: number;
  backupUsed: number;
  curtailment: number;
  bLoss: number;
  blokstroomImport: number;
  /**
   * MWh/jaar vraag die net, batterij én noodaggregaat niet konden dekken.
   * Hoort in de samenvatting omdat het financiële model er niet zonder kan:
   * `deliveredEnergy()` = `totalDelivered − unmet`. De lus rekende dit getal al
   * uit (`tUnmet`), maar `runSimulationForFinancials` liet het vallen — daardoor
   * factureerde de Hub-V2-businesscase de volledige vraag. Gemeten op de
   * Wognum-invoer (pv 2 MWp, 20 MWh/10 MW BESS, 0,33 MW aansluiting, geen
   * aggregaat, 19.000 MWh, jaar 15): 14.844,4 van 18.994,3 MWh ongedekt (78,2 %).
   */
  unmet: number;
}

// Hourly profile data for daily chart
export interface DailyHourData {
  pv: number; dem: number; bat: number; grid: number; gridBlok: number;
  bu: number; soc: number; n: number;
  bChargePV: number; bChargeBlok: number; bDischarge: number;
}

// Monthly aggregation
export interface MonthlyData {
  m: string; pv: number; dem: number; direct: number;
  bIn: number; bOut: number; gi: number; ge: number;
  bu: number; curt: number; unmet: number; mSS: number;
}

// Full simulation result (used by simulator visualization)
export interface SimResult extends SimSummary {
  pv: number;
  dem: number;
  demBaseTot: number;   // Total base load consumed
  demEvTot: number;     // Total EV charging consumed
  direct: number;
  bIn: number;
  bOut: number;
  gi: number;
  ge: number;
  bu: number;
  curt: number;
  unmet: number;
  ss: number;
  bh: number;
  monthly: MonthlyData[];
  daily: DailyHourData[][];
  bE: number;
  dod: number;
  pvUtil: number;
  effectiveBE: number;
  degPct: number;
  projYear: number;
  blokstroomImport: number;
}

/**
 * Run the full hub energy simulation.
 * Returns both summary data (for financial model) and detailed monthly/daily data (for visualization).
 */
export function runSimulation(p: SimParams): SimResult {
  const effectiveBE = p.bE * Math.pow(1 - p.deg / 100, p.projYear - 1);
  const sqEff = Math.sqrt(p.eff / 100);
  const usable = effectiveBE * (p.dod / 100);
  const scale = p.pv / p.solarRef;
  let soc = usable * 0.5;

  // Two-layer demand: base load + EV charging
  // Backwards compatible: if demBase is not set, fall back to legacy single-layer
  const useBase = p.demBase != null;
  const demBaseAnnual = useBase ? p.demBase : (p.dem ?? 0);
  const chpBase = useBase ? p.chpBase : (p.chp ?? Array(24).fill(1));
  const csfBase = useBase ? p.csfBase : (p.csf ?? Array(12).fill(1));
  const demEvAnnual = useBase ? p.demEv : 0;
  const chpEv = useBase ? p.chpEv : Array(24).fill(1);
  const csfEv = useBase ? p.csfEv : Array(12).fill(1);

  const chpBaseSum = chpBase.reduce((a, b) => a + b, 0);
  const csfBaseSum = csfBase.reduce((a, b) => a + b, 0);
  const normBase = 24 * 365.25 * (chpBaseSum / 24) * (csfBaseSum / 12);

  const chpEvSum = chpEv.reduce((a, b) => a + b, 0);
  const csfEvSum = csfEv.reduce((a, b) => a + b, 0);
  const normEv = 24 * 365.25 * (chpEvSum / 24) * (csfEvSum / 12);

  let tPV = 0, tDem = 0, tDirect = 0, tBIn = 0, tBOut = 0, tGI = 0, tGE = 0, tBU = 0, tCurt = 0, tUnmet = 0;
  let tBlokstroomImport = 0;
  let tDemBase = 0, tDemEv = 0;
  const monthly: MonthlyData[] = [];
  const daily: DailyHourData[][] = Array.from({ length: 12 }, () =>
    Array.from({ length: 24 }, () => ({
      pv: 0, dem: 0, bat: 0, grid: 0, gridBlok: 0, bu: 0, soc: 0, n: 0,
      bChargePV: 0, bChargeBlok: 0, bDischarge: 0,
    }))
  );

  for (let m = 0; m < 12; m++) {
    let mPV = 0, mDem = 0, mDirect = 0, mBI = 0, mBO = 0, mGI = 0, mGE = 0, mBU = 0, mCurt = 0, mUnmet = 0;
    for (let d = 0; d < MD[m]; d++) {
      for (let h = 0; h < 24; h++) {
        const pvH = p.SP[m][h] * scale / 1000;
        const demBaseH = normBase > 0 ? (demBaseAnnual * chpBase[h] * csfBase[m]) / normBase : 0;
        const demEvH = normEv > 0 ? (demEvAnnual * chpEv[h] * csfEv[m]) / normEv : 0;
        const demH = demBaseH + demEvH;
        tDemBase += demBaseH; tDemEv += demEvH;
        const net = pvH - demH;
        const effectiveGMax = isBlokstroomHour(h, p.blok) ? p.gMax + p.blok.power : p.gMax;

        let bIn = 0, bOut = 0, gi = 0, ge = 0, bu = 0, curt = 0, unmet = 0, direct = 0;

        if (net >= 0) {
          direct = demH;
          let surplus = net;
          const canCharge = Math.min(surplus, p.bP, (usable - soc) / sqEff);
          if (canCharge > 0) { bIn = canCharge; surplus -= canCharge; soc = Math.min(usable, soc + canCharge * sqEff); }
          const canExport = Math.min(surplus, p.gMax);
          if (canExport > 0) { ge = canExport; surplus -= canExport; }
          if (surplus > 0) curt = surplus;
        } else {
          direct = pvH;
          let deficit = -net;
          const rawDischarge = Math.min(deficit / sqEff, p.bP, soc);
          const canDischarge = rawDischarge * sqEff;
          if (canDischarge > 0) { bOut = canDischarge; deficit -= canDischarge; soc = Math.max(0, soc - rawDischarge); }
          const canImport = Math.min(deficit, effectiveGMax);
          if (canImport > 0) {
            gi = canImport; deficit -= canImport;
            if (isBlokstroomHour(h, p.blok)) {
              const baseImport = Math.min(canImport, p.gMax);
              tBlokstroomImport += canImport - baseImport;
            }
          }
          const canBU = Math.min(deficit, p.buP);
          if (canBU > 0) { bu = canBU; deficit -= canBU; }
          if (deficit > 0.0001) unmet = deficit;
        }

        // Proactief blokstroom laden
        if (isBlokstroomHour(h, p.blok) && soc < usable) {
          const chargeRoom = (usable - soc) / sqEff;
          const chargePowerLeft = Math.max(0, p.bP - bIn);
          const blokCharge = Math.min(p.blok.power, chargePowerLeft, chargeRoom);
          if (blokCharge > 0) {
            bIn += blokCharge; gi += blokCharge; soc += blokCharge * sqEff;
            tBlokstroomImport += blokCharge;
          }
        }

        soc = Math.max(0, Math.min(usable, soc));
        tPV += pvH; tDem += demH; tDirect += direct; tBIn += bIn; tBOut += bOut; tGI += gi; tGE += ge; tBU += bu; tCurt += curt; tUnmet += unmet;
        mPV += pvH; mDem += demH; mDirect += direct; mBI += bIn; mBO += bOut; mGI += gi; mGE += ge; mBU += bu; mCurt += curt; mUnmet += unmet;

        // Track battery charge sources
        let bChargePV = 0, bChargeBlok = 0;
        if (net >= 0) bChargePV = bIn;
        bChargeBlok = Math.max(0, bIn - bChargePV);

        const dd = daily[m][h];
        dd.pv += pvH; dd.dem += demH; dd.bat += (bOut - bIn); dd.grid += (gi - ge);
        dd.gridBlok += (isBlokstroomHour(h, p.blok) ? gi : 0);
        dd.bu += bu; dd.soc += soc; dd.n++; dd.bChargePV += bChargePV; dd.bChargeBlok += bChargeBlok; dd.bDischarge += bOut;
      }
    }
    const mLocal = mDirect + mBO;
    const mSS = mDem > 0 ? (mLocal / mDem) * 100 : 0;
    monthly.push({ m: MN[m], pv: mPV, dem: mDem, direct: mDirect, bIn: mBI, bOut: mBO, gi: mGI, ge: mGE, bu: mBU, curt: mCurt, unmet: mUnmet, mSS });
  }

  const local = tDirect + tBOut;
  const ss = tDem > 0 ? (local / tDem) * 100 : 0;
  const bLoss = tBIn - tBOut;
  const degPct = p.bE > 0 ? (effectiveBE / p.bE) * 100 : 100;

  return {
    // Summary (for financial model)
    pvProduction: tPV,
    totalDelivered: tDem,
    gridImport: tGI,
    gridExport: tGE,
    backupUsed: tBU,
    curtailment: tCurt,
    bLoss,
    blokstroomImport: tBlokstroomImport,
    // Detail (for visualization)
    pv: tPV, dem: tDem, demBaseTot: tDemBase, demEvTot: tDemEv,
    direct: tDirect, bIn: tBIn, bOut: tBOut,
    gi: tGI, ge: tGE, bu: tBU, curt: tCurt, unmet: tUnmet,
    ss, bh: p.buP > 0 ? tBU / p.buP : 0, monthly, daily,
    bE: p.bE, dod: p.dod,
    pvUtil: tPV > 0 ? ((tDirect + tBIn) / tPV * 100) : 0,
    effectiveBE, degPct, projYear: p.projYear,
  };
}

/**
 * Run simulation for financial model (business case).
 * Returns only the summary data needed by the financial calculations.
 */
export function runSimulationForFinancials(p: SimParams): SimSummary {
  const result = runSimulation(p);
  return {
    pvProduction: result.pvProduction,
    totalDelivered: result.totalDelivered,
    gridImport: result.gridImport,
    gridExport: result.gridExport,
    backupUsed: result.backupUsed,
    curtailment: result.curtailment,
    bLoss: result.bLoss,
    blokstroomImport: result.blokstroomImport,
    // Deze regel ontbrak. Zonder haar kreeg HubV2BusinessCase een sim zonder
    // `unmet`, gaf `deliveredEnergy()` NaN en toonde de hele tab €NaN; daarvóór
    // (met `totalDelivered` als omzetbasis) factureerde hij de 78,2 % ongedekte
    // vraag gewoon mee.
    unmet: result.unmet,
  };
}
